import { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * HorizontalCarousel — streaming-style horizontal scroller.
 *
 * Mobile: 2 cards visible, swipe to see more
 * Desktop: 4 cards visible
 *
 * Children should be individual card elements; each is wrapped in
 * a flex item with appropriate width.
 */
export default function HorizontalCarousel({
  children,
  title,
  icon,
  className = "",
  cardWidth = { mobile: 45, tablet: 30, desktop: 23 }, // % widths
  testid,
}) {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const items = Array.isArray(children) ? children.filter(Boolean) : [children].filter(Boolean);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [items.length]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.85 * (dir === "right" ? 1 : -1);
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  if (items.length === 0) return null;

  return (
    <div className={`relative ${className}`} data-testid={testid}>
      {(title || icon) && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2 text-lg">
            {icon}
            {title}
          </h3>
          <div className="hidden md:flex items-center gap-1">
            <button
              onClick={() => scrollBy("left")}
              disabled={!canLeft}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition ${canLeft ? "bg-white/10 hover:bg-white/20 text-white" : "bg-white/5 text-[#475569] cursor-not-allowed"}`}
              aria-label="Scroll left"
              data-testid={testid ? `${testid}-left` : undefined}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollBy("right")}
              disabled={!canRight}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition ${canRight ? "bg-white/10 hover:bg-white/20 text-white" : "bg-white/5 text-[#475569] cursor-not-allowed"}`}
              aria-label="Scroll right"
              data-testid={testid ? `${testid}-right` : undefined}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="relative">
        <div
          ref={scrollerRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none" }}
        >
          {items.map((child, i) => (
            <div
              key={i}
              className="flex-shrink-0 snap-start"
              style={{
                width: `${cardWidth.mobile}%`,
              }}
            >
              <div className="hidden sm:block md:hidden h-full" style={{ width: "100%" }}>{child}</div>
              <div className="sm:hidden h-full" style={{ width: "100%" }}>{child}</div>
              <div className="hidden md:block h-full">{child}</div>
            </div>
          ))}
        </div>
        {/* Fade edges for scroll affordance */}
        {canLeft && <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-[#0B1226] to-transparent" />}
        {canRight && <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-[#0B1226] to-transparent" />}
      </div>

      <style>{`
        @media (min-width: 640px) and (max-width: 1023px) {
          [data-testid="${testid}"] .flex-shrink-0 { width: ${cardWidth.tablet}% !important; }
        }
        @media (min-width: 1024px) {
          [data-testid="${testid}"] .flex-shrink-0 { width: ${cardWidth.desktop}% !important; }
        }
      `}</style>
    </div>
  );
}
